import debounce from "debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import TextInput from "../../../../components/inputs/textInput/TextInput";
import Loader from "../../../../components/loader/Loader";
import SendNotification from "../../../../components/sendNotification/SendNotification";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import {
  fetchUsersByQuery,
  resetSearchResults,
} from "../../../../features/userSearch/userSearchSlice";
import { fetchPaginatedUsers } from "../../../../features/users/usersSlice";
import styles from "./allUsers.module.css";
import OneUser from "./oneUser/OneUser";

const SEARCH_DEBOUNCE_MS = 450;

// All players + search on one page (same shape as All Squads). An empty box
// shows the full, infinitely scrolling list; typing swaps it for the matches
// from the dashboard player search (name / email / in-game ID / in-game name),
// which returns the top matches rather than pages, so scrolling only loads more
// while the box is empty.
const AllUsers = () => {
  const dispatch = useDispatch();
  const {
    list: users,
    fetchStatus,
    currentPage,
    totalPages,
  } = useSelector((state) => state.users);
  const { results: searchResults, status: searchStatus } = useSelector(
    (state) => state.userSearch,
  );
  const { rolePermissions } = useSelector((state) => state.user);

  const [searchValue, setSearchValue] = useState("");
  const searchTerm = searchValue.trim();
  const isSearching = Boolean(searchTerm);

  const userListRef = useRef();
  const debouncedSearchRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = userListRef.current;
    if (!container || fetchStatus === "loading" || isSearching) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      currentPage < totalPages
    ) {
      dispatch(fetchPaginatedUsers({ page: currentPage + 1 }));
    }
  }, [dispatch, fetchStatus, currentPage, totalPages, isSearching]);

  useEffect(() => {
    if (fetchStatus === "idle") {
      dispatch(fetchPaginatedUsers({ page: 1 }));
    }
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    const container = userListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Always a fresh search (like All Squads): the search cache isn't updated
  // by edits, so reading it could show a player's old details.
  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => {
      if (!value) {
        dispatch(resetSearchResults());
        return;
      }
      dispatch(fetchUsersByQuery({ query: value, kind: "user" }));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      debouncedSearchRef.current?.clear?.();
    };
  }, [dispatch]);

  // Leaving the page drops the search so the next visit starts on the list.
  useEffect(
    () => () => {
      dispatch(resetSearchResults());
    },
    [dispatch],
  );

  const handleSearch = useCallback((event) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearchRef.current?.(value.trim());
  }, []);

  const shownUsers = isSearching ? searchResults || [] : users;
  const isLoading = isSearching
    ? searchStatus === "loading"
    : fetchStatus === "loading";

  const displayUsers = shownUsers.map((item) => (
    <OneUser
      name={item?.name}
      email={item?.email}
      region={item?.region}
      userImage={item?.userImage}
      addedTime={item?.createdAt}
      modifiedTime={item?.updatedAt}
      key={item._id}
      userId={item._id}
      banned={item?.banned}
      gameIds={item?.gameIds}
      isPrime={item?.isPrime}
      emailVerified={item?.emailVerified}
      primeTier={item?.primeTier}
      primeExpiresAt={item?.primeExpiresAt}
      squadName={item?.squad?.name || ""}
      squadSlug={item?.squad?.slug || ""}
    />
  ));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <TextWrapper textStyle="textTitle">جميع اللاعبين</TextWrapper>
        {rolePermissions.includes("send-all-notification") && (
          <div className={styles.headerAction}>
            <SendNotification targetType={"all"} type={"general"} />
          </div>
        )}
      </div>
      <div className={styles.allUsersWrapper}>
        <DisplayContainer ref={userListRef} className={styles.usersDisplay}>
          <div className={styles.searchInputInnerWrapper}>
            <div className={styles.searchInputShell}>
              <TextInput
                height="50px"
                width="100%"
                borderRadius="12px"
                placeHolder="ابحث عن لاعب بالاسم أو البريد أو ايدي اللعبة"
                bgColor={"grey"}
                value={searchValue}
                onChange={handleSearch}
                bordered
              />
            </div>
          </div>

          {isSearching && searchStatus === "loading" ? null : displayUsers}

          {isLoading && (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          )}

          {!isLoading && isSearching && shownUsers.length === 0 && (
            <div className={styles.emptyState}>
              <TextWrapper textStyle="textMid">لا يوجد لاعبون مطابقون</TextWrapper>
            </div>
          )}
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllUsers;
